import React, { useEffect, useState } from "react";
import { CheckCircle2, Clock3, Languages, LoaderCircle, MapPin, Phone, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";

const inputClass = "mt-2 min-h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 dark:border-[#353b46] dark:bg-[#191d26] dark:text-white dark:placeholder:text-slate-500";

export default function GuideApplicationPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [application, setApplication] = useState(null);
  const [form, setForm] = useState({ phone: "", city: "", languages: "", experienceYears: "", introduction: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!supabase || !user?.id) { setLoading(false); return; }
      const { data, error: queryError } = await supabase.from("guide_verifications").select("*").eq("user_id", user.id).maybeSingle();
      if (!active) return;
      if (queryError) setError(t("work_portal.guide_load_error"));
      setApplication(data || null);
      if (data) setForm({ phone: data.phone || "", city: data.city || "", languages: data.languages || "", experienceYears: data.experience_years == null ? "" : String(data.experience_years), introduction: data.introduction || "" });
      setLoading(false);
    };
    load().catch(() => { if (active) { setError(t("work_portal.guide_load_error")); setLoading(false); } });
    return () => { active = false; };
  }, [t, user?.id]);

  useEffect(() => {
    if (application?.status !== "pending" || !supabase || !user?.id) return undefined;
    let active = true;
    const intervalId = window.setInterval(async () => {
      const { data, error: queryError } = await supabase.from("guide_verifications").select("*").eq("user_id", user.id).maybeSingle();
      if (active && !queryError && data) setApplication(data);
    }, 12000);
    return () => { active = false; window.clearInterval(intervalId); };
  }, [application?.status, user?.id]);

  const setField = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    if (!supabase || !user?.id) return;
    setSaving(true);
    setError("");
    const values = {
      phone: form.phone.trim(), city: form.city.trim(), languages: form.languages.trim(),
      experience_years: Number(form.experienceYears), introduction: form.introduction.trim(),
    };
    const result = application
      ? await supabase.from("guide_verifications").update(values).eq("user_id", user.id).select("*").single()
      : await supabase.from("guide_verifications").insert({ user_id: user.id, ...values }).select("*").single();
    setSaving(false);
    if (result.error) {
      console.error("Could not submit guide application", result.error);
      setError(t("work_portal.guide_submit_error"));
      return;
    }
    setApplication(result.data);
  };

  if (loading) return <div className="mt-8 flex items-center justify-center gap-3 rounded-3xl border border-slate-200 bg-white p-8 text-sm text-slate-500 dark:border-[#272c35] dark:bg-[#11141b] dark:text-slate-400"><LoaderCircle className="h-5 w-5 animate-spin text-amber-500 dark:text-amber-400" />{t("work_portal.loading")}</div>;

  if (application?.status === "pending") return (
    <section className="mt-8 rounded-3xl border border-amber-200 bg-amber-50 p-6 dark:border-amber-400/20 dark:bg-amber-400/5 sm:p-8">
      <Clock3 className="h-9 w-9 text-amber-500 dark:text-amber-400" aria-hidden="true" />
      <h2 className="mt-4 text-xl font-extrabold text-slate-900 dark:text-white">{t("work_portal.application_pending_title")}</h2>
      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">{t("work_portal.application_pending_text")}</p>
    </section>
  );

  return (
    <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-5 dark:border-[#272c35] dark:bg-[#11141b] sm:p-8">
      <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">{t("work_portal.guide_application_title")}</h2>
      <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">{t("work_portal.guide_application_text")}</p>
      {application?.status === "rejected" && <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-800 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-200"><strong>{t("work_portal.application_rejected_title")}</strong>{application.admin_note && <p className="mt-1">{application.admin_note}</p>}</div>}
      {error && <p role="alert" className="mt-5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-200">{error}</p>}
      <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-bold text-slate-700 dark:text-slate-200"><span className="inline-flex items-center gap-2">{t("work_portal.phone")} <Phone className="h-4 w-4 text-amber-500 dark:text-amber-400" /></span><input className={inputClass} type="tel" required maxLength={32} value={form.phone} onChange={setField("phone")} placeholder="+998 90 123 45 67" /></label>
        <label className="text-sm font-bold text-slate-700 dark:text-slate-200"><span className="inline-flex items-center gap-2">{t("work_portal.city")} <MapPin className="h-4 w-4 text-amber-500 dark:text-amber-400" /></span><input className={inputClass} required minLength={2} maxLength={80} value={form.city} onChange={setField("city")} placeholder={t("work_portal.city_placeholder")} /></label>
        <label className="text-sm font-bold text-slate-700 dark:text-slate-200"><span className="inline-flex items-center gap-2">{t("work_portal.languages")} <Languages className="h-4 w-4 text-amber-500 dark:text-amber-400" /></span><input className={inputClass} required minLength={2} maxLength={160} value={form.languages} onChange={setField("languages")} placeholder={t("work_portal.languages_placeholder")} /></label>
        <label className="text-sm font-bold text-slate-700 dark:text-slate-200">{t("work_portal.experience_years")}<input className={inputClass} type="number" required min="0" max="70" value={form.experienceYears} onChange={setField("experienceYears")} /></label>
        <label className="text-sm font-bold text-slate-700 dark:text-slate-200 sm:col-span-2">{t("work_portal.introduction")}<textarea className={`${inputClass} min-h-32 resize-y`} required minLength={20} maxLength={1500} value={form.introduction} onChange={setField("introduction")} placeholder={t("work_portal.introduction_placeholder")} /></label>
        <button type="submit" disabled={saving} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-amber-400 px-5 text-sm font-extrabold text-slate-950 transition hover:bg-amber-300 disabled:opacity-60 sm:col-span-2">
          {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : application?.status === "rejected" ? <CheckCircle2 className="h-4 w-4" /> : <Send className="h-4 w-4" />}
          {saving ? t("work_portal.saving") : application?.status === "rejected" ? t("work_portal.resubmit") : t("work_portal.submit")}
        </button>
      </form>
    </section>
  );
}
